import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { auth, db, storage, functions } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import downscaleImage from './downscaleImage';

// Usernames that collide with app routes or would be misleading. Blocked at signup.
const RESERVED_USERNAMES = new Set([
  'admin', 'me', 'settings', 'create-profile', 'invite', 'privacy', 'terms',
  'login', 'signup', 'api', 'about', 'help', 'support', 'home', 'profile',
  'user', 'users', 'yourtop8', 'top8', 'www', 'root', 'mod', 'moderator',
  'official', 'verified', 'notifications', 'people', 'connections',
  'legal', 'trademark', 'contact', 'business', 'feedback', 'demo',
]);

function CreateProfile({ onProfileCreated, onPhotoUploaded }) {
  const user = auth.currentUser;
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [bio, setBio] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user?.photoURL || null);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'short'
  const [submitting, setSubmitting] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const debounceRef = useRef();

  // Debounced username availability check
  useEffect(() => {
    if (!username) { setUsernameStatus(null); return; }
    if (username.length < 3) { setUsernameStatus('short'); return; }
    if (RESERVED_USERNAMES.has(username.toLowerCase())) { setUsernameStatus('reserved'); return; }
    setUsernameStatus('checking');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const snap = await getDoc(doc(db, 'usernames', username.toLowerCase()));
      setUsernameStatus(snap.exists() ? 'taken' : 'available');
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [username]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || usernameStatus !== 'available') return;
    if (!displayName.trim()) { setError('Display name is required.'); return; }
    if (!ageConfirmed) { setError('You must confirm you are 18 or older.'); return; }
    setSubmitting(true);
    setError('');

    try {
      let finalPhotoURL = user.photoURL || null;
      if (photoFile) {
        const photoStorageRef = storageRef(storage, `profile-pictures/${user.uid}`);
        const blob = await downscaleImage(photoFile, 512);
        await uploadBytes(photoStorageRef, blob);
        finalPhotoURL = await getDownloadURL(photoStorageRef);
        if (onPhotoUploaded) onPhotoUploaded(finalPhotoURL);
      }

      const cleanUsername = username.toLowerCase().trim();

      // Force a fresh ID token so the new email_verified claim is present. Right
      // after verifying, the cached token still says email_verified=false, which
      // would make createUserProfile reject even though the user IS verified.
      try { await auth.currentUser?.getIdToken(true); } catch { /* ignore */ }

      // Profile + username are claimed atomically server-side (createUserProfile):
      // one username per account, verified email required, no half-written state.
      const submit = httpsCallable(functions, 'createUserProfile');
      await submit({
        username: cleanUsername,
        displayName: displayName.trim(),
        bio: bio.trim(),
        photoURL: finalPhotoURL,
        ageConfirmed: true,
      });
      onProfileCreated();
    } catch (err) {
      console.error(err);
      const code = err?.code || '';
      if (code.includes('already-exists')) {
        setError('That username was just taken. Please choose another.');
        setUsernameStatus('taken');
      } else if (code.includes('failed-precondition')) {
        setError('You already have a profile.');
      } else if (code.includes('permission-denied')) {
        setError('Please verify your email before creating a profile.');
      } else if (code.includes('invalid-argument')) {
        setError('That username isn\'t allowed.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const usernameHint = () => {
    if (!username) return <span className="form-hint">yourtop8.com/{username || 'yourname'}</span>;
    if (usernameStatus === 'short') return <span className="form-hint">At least 3 characters</span>;
    if (usernameStatus === 'reserved') return <span className="form-error">✗ That username is reserved</span>;
    if (usernameStatus === 'checking') return <span className="form-hint">Checking availability…</span>;
    if (usernameStatus === 'available') return <span className="form-ok">✓ Available</span>;
    if (usernameStatus === 'taken') return <span className="form-error">✗ Already taken</span>;
    return null;
  };

  const canSubmit = username.length >= 3 && usernameStatus === 'available' && displayName.trim() && ageConfirmed && !submitting;

  return (
    <div className="create-profile-page">
      <div className="create-profile-card">
        <h2>Create Your Profile</h2>
        <p className="subtitle">Set up your account so friends can find and add you.</p>

        {/* Photo picker */}
        <div className="photo-picker">
          <div
            className="photo-picker-avatar"
            onClick={() => fileInputRef.current?.click()}
            title="Change photo"
          >
            {photoPreview
              ? <img src={photoPreview} alt="preview" />
              : <span>📷</span>
            }
          </div>
          <div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose photo
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, marginBottom: 0 }}>
              JPG, PNG or GIF. Max 5MB.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Display Name</label>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How you appear to others"
              required
            />
          </div>

          <div className="form-group">
            <label>Bio <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
            <input
              className="input"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="A short description about yourself"
              maxLength={160}
            />
          </div>

          <div className="form-group">
            <label>Username</label>
            <div className="username-row">
              <span className="username-prefix">yourtop8.com/</span>
              <input
                value={username}
                onChange={(e) =>
                  setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
                }
                placeholder="yourname"
                required
                maxLength={30}
              />
            </div>
            {usernameHint()}
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '4px 0 14px', cursor: 'pointer', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={e => setAgeConfirmed(e.target.checked)}
              style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
            />
            <span>
              I confirm I am 18 years of age or older and agree to the{' '}
              <Link to="/terms" target="_blank" style={{ color: 'var(--accent)' }}>Terms</Link> and{' '}
              <Link to="/privacy" target="_blank" style={{ color: 'var(--accent)' }}>Privacy Policy</Link>.
            </span>
          </label>

          {error && <p className="form-error" style={{ marginBottom: 14 }}>{error}</p>}

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            disabled={!canSubmit}
            style={{ marginTop: 8 }}
          >
            {submitting ? <span className="spinner" /> : 'Create Profile →'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreateProfile;
